import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus,
  Search,
  Calendar,
  User,
  MoreVertical,
  X,
  Edit,
  Trash2,
  Play,
  Pause,
  Check,
  RefreshCw,
  Eye,
  ClipboardCheck,
  RotateCcw
} from 'lucide-react';
import { workOrderService, WORK_ORDER_STATUSES, WORK_ORDER_TYPES, WORK_ORDER_PRIORITIES } from '../../../services/workOrderService';
import { assetService } from '../../../services/assetService';
import { teamService } from '../../../services/teamService';
import { useCmmsAuth } from '../../../contexts/CmmsAuthContext';
import { PERMISSIONS } from '../../../constants/permissions';
import PermissionGuard from '../../../components/PermissionGuard';
import { useDateFormat } from '../../../hooks/useDateFormat';
import { useTranslation } from 'react-i18next';

const WorkOrders = () => {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState('kanban');
  const [searchTerm, setSearchTerm] = useState('');
  const [workOrders, setWorkOrders] = useState([]);
  const [stats, setStats] = useState({});
  const [assets, setAssets] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState(null);

  // Permission-based controls
  const { hasPermission } = useCmmsAuth();
  const canCreate = hasPermission(PERMISSIONS.CREATE_WORK_ORDERS);
  const canEdit = hasPermission(PERMISSIONS.EDIT_WORK_ORDERS);
  const canDelete = hasPermission(PERMISSIONS.DELETE_WORK_ORDERS);
  const canComplete = hasPermission(PERMISSIONS.COMPLETE_WORK_ORDERS);

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingWorkOrder, setEditingWorkOrder] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    assetId: '',
    type: 'CORRECTIVE',
    priority: 'MEDIUM',
    assignedTo: '',
    scheduledStart: '',
    scheduledEnd: '',
    estimatedHours: '',
  });

  // Action menu
  const [activeMenu, setActiveMenu] = useState(null);
  const { formatDate } = useDateFormat();

  // Note modal — replaces window.prompt() for status-change notes
  const [noteModal, setNoteModal] = useState(null);
  // noteModal shape: { title, placeholder, required, onConfirm }
  const [noteValue, setNoteValue] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [ordersRes, statsRes, assetsRes] = await Promise.all([
        workOrderService.getWorkOrders(),
        workOrderService.getStats(),
        assetService.getAssets(),
      ]);
      setWorkOrders(ordersRes || []);
      setStats(statsRes || {});
      setAssets(assetsRes || []);

      // Try to fetch users
      try {
        const usersRes = await teamService.getTeamMembers();
        setUsers(usersRes || []);
      } catch (e) {
        console.log('Could not fetch team members');
      }
    } catch (err) {
      setError('Failed to load work orders');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        assetId: parseInt(formData.assetId),
        assignedTo: formData.assignedTo ? parseInt(formData.assignedTo) : null,
        estimatedHours: formData.estimatedHours ? parseFloat(formData.estimatedHours) : null,
        scheduledStart: formData.scheduledStart ? `${formData.scheduledStart}T00:00:00` : null,
        scheduledEnd: formData.scheduledEnd ? `${formData.scheduledEnd}T23:59:59` : null,
      };

      if (editingWorkOrder) {
        await workOrderService.updateWorkOrder(editingWorkOrder.id, payload);
      } else {
        await workOrderService.createWorkOrder(payload);
      }

      setShowModal(false);
      resetForm();
      fetchData();
    } catch (err) {
      console.error('Failed to save work order:', err);
      alert('Failed to save work order');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      assetId: '',
      type: 'CORRECTIVE',
      priority: 'MEDIUM',
      assignedTo: '',
      scheduledStart: '',
      scheduledEnd: '',
      estimatedHours: '',
    });
    setEditingWorkOrder(null);
  };

  const handleEdit = (workOrder) => {
    setEditingWorkOrder(workOrder);
    setFormData({
      title: workOrder.title || '',
      description: workOrder.description || '',
      assetId: workOrder.assetId || '',
      type: workOrder.type || 'CORRECTIVE',
      priority: workOrder.priority || 'MEDIUM',
      assignedTo: workOrder.assignedTo || '',
      scheduledStart: workOrder.scheduledStart ? workOrder.scheduledStart.split('T')[0] : '',
      scheduledEnd: workOrder.scheduledEnd ? workOrder.scheduledEnd.split('T')[0] : '',
      estimatedHours: workOrder.estimatedHours || '',
    });
    setShowModal(true);
    setActiveMenu(null);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this work order?')) {
      try {
        await workOrderService.deleteWorkOrder(id);
        fetchData();
      } catch (err) {
        console.error('Failed to delete:', err);
        alert('Failed to delete work order');
      }
    }
    setActiveMenu(null);
  };

  const openNoteModal = (config) => {
    setNoteValue('');
    setNoteModal(config);
  };

  const closeNoteModal = () => {
    setNoteModal(null);
    setNoteValue('');
  };

  const handleStatusChange = async (id, newStatus) => {
    const doUpdate = async (notes) => {
      try {
        await workOrderService.updateStatus(id, newStatus, notes || null);
        fetchData();
      } catch (err) {
        console.error('Failed to update status:', err);
        alert(err.message || 'Failed to update status');
      }
      setActiveMenu(null);
    };

    if (newStatus === 'PENDING_REVIEW') {
      openNoteModal({
        title: t('cmms.wo_submitForReview'),
        placeholder: t('cmms.wod_placeholderReview'),
        required: true,
        onConfirm: doUpdate,
      });
    } else if (newStatus === 'ON_HOLD') {
      openNoteModal({
        title: t('cmms.wo_putOnHold'),
        placeholder: t('cmms.wod_placeholderHold'),
        required: false,
        onConfirm: doUpdate,
      });
    } else if (newStatus === 'IN_PROGRESS') {
      openNoteModal({
        title: t('cmms.wod_modalStartTitle'),
        placeholder: t('cmms.wod_placeholderStart'),
        required: false,
        onConfirm: doUpdate,
      });
    } else {
      await doUpdate(null);
    }
  };

  const handleComplete = async (id) => {
    openNoteModal({
      title: t('cmms.wod_modalCompleteTitle'),
      placeholder: t('cmms.wod_placeholderComplete'),
      required: false,
      onConfirm: async (notes) => {
        try {
          await workOrderService.completeWorkOrder(id, notes || null);
          fetchData();
        } catch (err) {
          console.error('Failed to complete:', err);
          alert('Failed to complete work order');
        }
        setActiveMenu(null);
      },
    });
  };

  const statusColumns = {
    ASSIGNED: { title: t('cmms.assigned'), color: 'bg-blue-500' },
    IN_PROGRESS: { title: t('cmms.inProgress'), color: 'bg-yellow-500' },
    ON_HOLD: { title: t('cmms.onHold'), color: 'bg-orange-500' },
    PENDING_REVIEW: { title: t('cmms.pendingReview'), color: 'bg-purple-500' },
    COMPLETED: { title: t('cmms.completed'), color: 'bg-green-500' },
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'URGENT': return 'bg-red-100 text-red-800 border-red-200';
      case 'HIGH': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'MEDIUM': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'LOW': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'OPEN': return 'bg-gray-100 text-gray-800';
      case 'ASSIGNED': return 'bg-blue-100 text-blue-800';
      case 'IN_PROGRESS': return 'bg-yellow-100 text-yellow-800';
      case 'ON_HOLD': return 'bg-orange-100 text-orange-800';
      case 'PENDING_REVIEW': return 'bg-purple-100 text-purple-800';
      case 'COMPLETED': return 'bg-green-100 text-green-800';
      case 'CANCELLED': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredOrders = workOrders.filter(order => {
    const matchesSearch = !searchTerm ||
      order.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.woNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.assetName?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = !statusFilter || order.status === statusFilter;
    const matchesPriority = !priorityFilter || order.priority === priorityFilter;
    const matchesType = !typeFilter || order.type === typeFilter;

    return matchesSearch && matchesStatus && matchesPriority && matchesType;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <PermissionGuard permission={PERMISSIONS.VIEW_WORK_ORDERS}>
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('cmms.wo_title')}</h1>
          <p className="text-gray-600 mt-1">{t('cmms.wo_subtitle')}</p>
        </div>
        <div className="flex gap-3">
          <button className="btn btn-outline" onClick={fetchData}>
            <RefreshCw className="w-5 h-5 mr-2" />
            {t('common.refresh')}
          </button>
          {canCreate && (
            <button
              className="btn btn-primary"
              onClick={() => { resetForm(); setShowModal(true); }}
            >
              <Plus className="w-5 h-5 mr-2" />
              {t('cmms.wo_addWorkOrder')}
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="text-2xl font-bold text-gray-900">{stats.active || 0}</div>
          <div className="text-sm text-gray-600">{t('cmms.wo_totalActive')}</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="text-2xl font-bold text-yellow-600">{(stats.open || 0) + (stats.assigned || 0)}</div>
          <div className="text-sm text-gray-600">{t('cmms.wo_pending')}</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="text-2xl font-bold text-blue-600">{stats.inProgress || 0}</div>
          <div className="text-sm text-gray-600">{t('cmms.inProgress')}</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="text-2xl font-bold text-green-600">{stats.completedToday || 0}</div>
          <div className="text-sm text-gray-600">{t('cmms.completedToday')}</div>
        </div>
      </div>

      {/* Filters & View Toggle */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          {/* Search */}
          <div className="flex-1 min-w-[300px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder={t('common.search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          {/* Filters */}
          <select
            className="px-4 py-2 border border-gray-300 rounded-lg"
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
          >
            <option value="">{t('cmms.wo_allPriorities')}</option>
            {WORK_ORDER_PRIORITIES.map(p => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>

          <select
            className="px-4 py-2 border border-gray-300 rounded-lg"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">{t('cmms.wo_allStatuses')}</option>
            {WORK_ORDER_STATUSES.map(s => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>

          <select
            className="px-4 py-2 border border-gray-300 rounded-lg"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="">{t('common.allTypes')}</option>
            {WORK_ORDER_TYPES.map(wot => (
              <option key={wot.value} value={wot.value}>{wot.label}</option>
            ))}
          </select>

          {/* View Toggle */}
          <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('kanban')}
              className={`px-3 py-1 rounded text-sm ${viewMode === 'kanban' ? 'bg-white shadow-sm' : ''}`}
            >
              {t('cmms.wo_kanban')}
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-1 rounded text-sm ${viewMode === 'list' ? 'bg-white shadow-sm' : ''}`}
            >
              {t('cmms.wo_list')}
            </button>
          </div>
        </div>
      </div>

      {/* Work Orders Display */}
      {viewMode === 'kanban' ? (
        /* Kanban Board */
        <div className="grid md:grid-cols-5 gap-4">
          {Object.entries(statusColumns).map(([status, config]) => (
            <div key={status} className="flex flex-col">
              <div className="flex items-center gap-2 mb-4">
                <div className={`w-3 h-3 ${config.color} rounded-full`}></div>
                <h3 className="font-semibold text-gray-900 text-sm">{config.title}</h3>
                <span className="ml-auto text-sm text-gray-500">
                  {filteredOrders.filter(o => o.status === status).length}
                </span>
              </div>
              <div className="space-y-3 flex-1 min-h-[200px]">
                {filteredOrders
                  .filter(order => order.status === status)
                  .map((order) => (
                    <div
                      key={order.id}
                      className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <span className="text-xs font-medium text-gray-500">{order.woNumber}</span>
                          <h4 className="font-semibold text-gray-900 text-sm mt-1 line-clamp-2">{order.title}</h4>
                        </div>
                        <div className="relative">
                          <button
                            className="text-gray-400 hover:text-gray-600"
                            onClick={() => setActiveMenu(activeMenu === order.id ? null : order.id)}
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>
                          {activeMenu === order.id && (
                            <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                              <Link
                                to={`/portal/cmms/work-orders/${order.id}`}
                                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                                onClick={() => setActiveMenu(null)}
                              >
                                <Eye className="w-4 h-4" /> {t('cmms.wo_viewDetails')}
                              </Link>
                              {canEdit && (
                                <button
                                  onClick={() => handleEdit(order)}
                                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                                >
                                  <Edit className="w-4 h-4" /> {t('common.edit')}
                                </button>
                              )}
                              {canEdit && order.status !== 'IN_PROGRESS' && order.status !== 'COMPLETED' && order.status !== 'PENDING_REVIEW' && (
                                <button
                                  onClick={() => handleStatusChange(order.id, 'IN_PROGRESS')}
                                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                                >
                                  <Play className="w-4 h-4" /> {t('cmms.wo_startWork')}
                                </button>
                              )}
                              {canEdit && order.status === 'IN_PROGRESS' && (
                                <>
                                  <button
                                    onClick={() => handleStatusChange(order.id, 'ON_HOLD')}
                                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                                  >
                                    <Pause className="w-4 h-4" /> {t('cmms.wo_putOnHold')}
                                  </button>
                                  {/* Submit for Review - available for all editors */}
                                  <button
                                    onClick={() => handleStatusChange(order.id, 'PENDING_REVIEW')}
                                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-purple-600"
                                  >
                                    <ClipboardCheck className="w-4 h-4" /> {t('cmms.wo_submitForReview')}
                                  </button>
                                  {/* Complete - only for users with COMPLETE_WORK_ORDERS permission */}
                                  {canComplete && (
                                    <button
                                      onClick={() => handleComplete(order.id)}
                                      className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-green-600"
                                    >
                                      <Check className="w-4 h-4" /> {t('cmms.wod_complete')}
                                    </button>
                                  )}
                                </>
                              )}
                              {canEdit && order.status === 'ON_HOLD' && (
                                <button
                                  onClick={() => handleStatusChange(order.id, 'IN_PROGRESS')}
                                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                                >
                                  <Play className="w-4 h-4" /> {t('cmms.wo_resume')}
                                </button>
                              )}
                              {/* PENDING_REVIEW actions - only for users with COMPLETE permission */}
                              {order.status === 'PENDING_REVIEW' && canComplete && (
                                <>
                                  <button
                                    onClick={() => handleComplete(order.id)}
                                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-green-600"
                                  >
                                    <Check className="w-4 h-4" /> {t('cmms.wo_approveComplete')}
                                  </button>
                                  <button
                                    onClick={() => handleStatusChange(order.id, 'IN_PROGRESS')}
                                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-orange-600"
                                  >
                                    <RotateCcw className="w-4 h-4" /> {t('cmms.wo_returnForRework')}
                                  </button>
                                </>
                              )}
                              {canDelete && order.status !== 'COMPLETED' && (
                                <button
                                  onClick={() => handleDelete(order.id)}
                                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-red-600"
                                >
                                  <Trash2 className="w-4 h-4" /> {t('common.delete')}
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {order.assetName && (
                        <p className="text-xs text-gray-600 mb-2">{order.assetName}</p>
                      )}

                      <div className="flex flex-wrap gap-1 mb-2">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium border ${getPriorityColor(order.priority)}`}>
                          {order.priority}
                        </span>
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                          {order.type}
                        </span>
                      </div>

                      <div className="flex items-center justify-between text-xs text-gray-500 mt-2">
                        {order.assignedToName ? (
                          <div className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            <span className="truncate max-w-[80px]">{order.assignedToName}</span>
                          </div>
                        ) : (
                          <span className="text-gray-400">{t('cmms.unassigned')}</span>
                        )}
                        {order.scheduledStart && (
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            <span>{formatDate(order.scheduledStart)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* List View */
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('cmms.wo_number')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('common.name')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('cmms.wo_asset')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('common.type')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('common.priority')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('common.status')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('cmms.wo_assignedTo')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('common.date')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-24">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredOrders.map((order) => (
                <tr key={order.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                    {order.woNumber}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 max-w-[200px] truncate">
                    {order.title}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                    {order.assetName || '-'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                    {order.type}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`px-2 py-1 rounded text-xs font-medium border ${getPriorityColor(order.priority)}`}>
                      {order.priority}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(order.status)}`}>
                      {order.status?.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                    {order.assignedToName || '-'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                    {order.scheduledStart ? formatDate(order.scheduledStart) : '-'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm w-24">
                    <div className="flex items-center gap-3">
                      <Link
                        to={`/portal/cmms/work-orders/${order.id}`}
                        className="text-gray-600 hover:text-gray-800"
                        title={t('cmms.wo_viewDetails')}
                      >
                        <Eye className="w-4 h-4" />
                      </Link>
                      {canEdit && (
                        <button
                          onClick={() => handleEdit(order)}
                          className="text-blue-600 hover:text-blue-800"
                          title={t('common.edit')}
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                      )}
                      {canDelete && order.status !== 'COMPLETED' && (
                        <button
                          onClick={() => handleDelete(order.id)}
                          className="text-red-600 hover:text-red-800"
                          title={t('common.delete')}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredOrders.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              {t('cmms.wo_noResults')}
            </div>
          )}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold">
                {editingWorkOrder ? t('common.edit') : t('cmms.wo_addWorkOrder')}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.name')} *</label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="Enter work order title"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.description')}</label>
                <textarea
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="Enter description"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.wo_asset')} *</label>
                  <select
                    required
                    value={formData.assetId}
                    onChange={(e) => setFormData({...formData, assetId: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">{t('cmms.wo_selectAsset')}</option>
                    {assets.map(asset => (
                      <option key={asset.id} value={asset.id}>{asset.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.wo_assignedTo')}</label>
                  <select
                    value={formData.assignedTo}
                    onChange={(e) => setFormData({...formData, assignedTo: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">{t('cmms.unassigned')}</option>
                    {users.map(user => (
                      <option key={user.id} value={user.id}>{user.fullName || user.email}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.type')} *</label>
                  <select
                    required
                    value={formData.type}
                    onChange={(e) => setFormData({...formData, type: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    {WORK_ORDER_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.priority')} *</label>
                  <select
                    required
                    value={formData.priority}
                    onChange={(e) => setFormData({...formData, priority: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    {WORK_ORDER_PRIORITIES.map(p => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.wo_estHours')}</label>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    value={formData.estimatedHours}
                    onChange={(e) => setFormData({...formData, estimatedHours: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    placeholder="0"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.wo_scheduledStart')}</label>
                  <input
                    type="date"
                    value={formData.scheduledStart}
                    onChange={(e) => setFormData({...formData, scheduledStart: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.wo_scheduledEnd')}</label>
                  <input
                    type="date"
                    value={formData.scheduledEnd}
                    onChange={(e) => setFormData({...formData, scheduledEnd: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  {t('common.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Note Modal — replaces window.prompt() for status-change notes */}
      {noteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">{noteModal.title}</h2>
              <button onClick={closeNoteModal} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">Notes</label>
                  {noteModal.required ? (
                    <span className="text-xs font-medium text-red-600 bg-red-50 px-2 py-0.5 rounded">Required</span>
                  ) : (
                    <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded">Optional</span>
                  )}
                </div>
                <textarea
                  rows={4}
                  value={noteValue}
                  onChange={(e) => setNoteValue(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 resize-none"
                  placeholder={noteModal.placeholder}
                  autoFocus
                />
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={closeNoteModal}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (noteModal.required && !noteValue.trim()) return;
                    const val = noteValue.trim() || null;
                    const onConfirm = noteModal.onConfirm;
                    closeNoteModal();
                    onConfirm(val);
                  }}
                  disabled={noteModal.required && !noteValue.trim()}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Click outside to close menu */}
      {activeMenu && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setActiveMenu(null)}
        />
      )}
    </div>
    </PermissionGuard>
  );
};

export default WorkOrders;
